import styles from '@/styles/components/EditorialContentCard.module.scss';

import { generateImageUrl } from '@/lib/sanity/image';
import { EditorialContent, isWrittenContent } from '@/types/content';
import Image from 'next/image';
import Link from 'next/link';
import { generateLocalizedURL } from '@/utils/url';

type CardVariant =
  | 'hero'
  | 'highlight'
  | 'urgent'
  | 'featured'
  | 'promoted'
  | 'pinned'
  | 'default'
  | 'secondary'
  | 'minimal'
  | 'compact'
  | 'list'
  | 'card'
  | 'grid'
  | 'inline'
  | 'interactive'
  | 'overlay'
  | 'opinion'
  | 'hidden';

type CardLayoutOptions = {
  orientation?: 'horizontal' | 'vertical';
  direction?: 'normal' | 'reverse';
  aspectRatio?: '1/1' | '3/2' | '4/3' | '16/9' | 'auto';
  size?: 'small' | 'medium' | 'large';
  showImage?: boolean;
  showLead?: boolean;
  showAuthor?: boolean;
  showPublishedAt?: boolean;
  showTag?: boolean;
  showReadingTime?: boolean;
  customClassNames?: string;
};

type EditorialContentCardProps = {
  variant?: CardVariant;
  content: EditorialContent;
  layout?: CardLayoutOptions;
};

const DEFAULT_LAYOUT: CardLayoutOptions = {
  orientation: 'vertical',
  aspectRatio: '3/2',
  size: 'medium',
  showImage: true,
  showLead: false,
  showAuthor: false,
  showPublishedAt: false,
  showTag: true,
  showReadingTime: false,
};

const resolveImageResolution = (
  aspectRatio: CardLayoutOptions['aspectRatio'],
  size: CardLayoutOptions['size']
) => {
  const resolutions: Record<string, Record<string, { width: number; height: number }>> = {
    '3/2': { small: { width: 300, height: 200 }, medium: { width: 600, height: 400 }, large: { width: 1200, height: 800 } },
    '1/1': { small: { width: 100, height: 100 }, medium: { width: 200, height: 200 }, large: { width: 600, height: 600 } },
    '16/9': { small: { width: 320, height: 180 }, medium: { width: 480, height: 270 }, large: { width: 960, height: 540 } },
    '4/3': { small: { width: 400, height: 300 }, medium: { width: 800, height: 600 }, large: { width: 1600, height: 1200 } },
    auto: { small: { width: 300, height: 300 }, medium: { width: 600, height: 600 }, large: { width: 1200, height: 1200 } },
  };

  return resolutions[aspectRatio || '3/2'][size || 'medium'];
};

const EditorialContentCard: React.FC<EditorialContentCardProps> = ({
  variant,
  content,
  layout = {},
}) => {
  const {
    orientation = 'vertical',
    direction = 'normal',
    aspectRatio = '3/2',
    size = 'medium',
    showImage = true,
    showLead,
    showAuthor,
    showPublishedAt,
    showTag,
    showReadingTime,
    customClassNames,
  } = { ...DEFAULT_LAYOUT, ...layout };

  if (!isWrittenContent(content)) {
    console.warn('EditorialContentCard expects content of type WrittenContent.');
    return null;
  }

  const renderImage = () => {
    const resolution = resolveImageResolution(aspectRatio, size);
    const imageUrl = content.image ? generateImageUrl(content.image, resolution.width, resolution.height) : null;
    const aspectClass = aspectRatio !== 'auto' ? `aspect-${aspectRatio.replace('/', '-')}` : '';
    return imageUrl ? (
      <figure className={`${styles.mediaContainer} ${styles[aspectClass]}`}>
        <Image
          src={imageUrl}
          alt={content.headline || 'Content image'}
          className={styles.image}
          fill
        />
      </figure>
    ) : <div className={`${styles.mediaContainer} ${styles[aspectClass]}`} />;
  };

  const renderTag = () => content.tags?.length && (
    <strong className={styles.tag} style={{ color: content.tags[0].color }}>
      {content.tags[0].title}
    </strong>
  );

  const renderAuthor = () => content.authors?.length && (
    <div className={styles.authors}>
      <div className={styles.author}>
        <p className={styles.authorName}>{content.authors[0].name}</p>
      </div>
    </div>
  );

  const renderAuthorImage = () => {
    if (!content.authors?.length) return null;
    const authorImage = generateImageUrl(content.authors[0].image, 96, 96);
    return authorImage ? (
      <figure className={`${styles.mediaContainer} ${styles.aspectSquare}`}>
        <Image
          src={authorImage}
          alt={content.authors[0].name || 'Author image'}
          className={styles.authorImage}
          fill
        />
      </figure>
    ) : <div className={`${styles.mediaContainer} ${styles.aspectSquare}`} />;
  };

  return (
    <article className={`${styles.card} ${styles[`card--${variant}`]} ${styles[orientation]} ${styles[direction]} ${customClassNames}`}>
      {showImage && (variant === 'opinion' ? renderAuthorImage() : renderImage())}
      <main className={styles.content}>
        <header>
          {showTag && renderTag()}
          <Link
            href={content.shorthandUrl ? content.shorthandUrl : generateLocalizedURL(content.slug, content.language, false)}
            className={styles.link}
            target={content.shorthandUrl ? '_blank' : undefined}
            prefetch={false}
          >
            <h3 className={styles.headline}>
              {content.headline}
            </h3>
          </Link>
        </header>
        {showLead && content.lead && <p className={styles.lead}>{content.lead}</p>}
        {showAuthor && renderAuthor()}
      </main>
    </article>
  )

};

export default EditorialContentCard;